/**
 * The gtag.js layer: GA4 events, Google Ads conversions and Consent Mode.
 *
 * The tag itself is bootstrapped by the inline script in public/index.html —
 * including the Consent Mode v2 defaults, which have to be in `dataLayer` before
 * the library initialises. Everything here runs later and only ever pushes
 * commands onto the queue that script created.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: unknown[];
  }
}

/** Values gtag.js accepts as event parameters. `undefined` entries are dropped. */
export type EventParams = Record<string, string | number | boolean | undefined>;

/**
 * The funnel milestones we treat as conversions.
 *
 * `sign_up` and `purchase` are GA4 recommended event names and must keep those
 * spellings — GA4 gives recommended events reporting and audience behaviour that
 * custom names do not get. `trial_started` and `project_created` have no
 * recommended equivalent.
 *
 * `api_key_created` and `activation_complete` are the two /get-started
 * milestones. An API key exists for one purpose — authenticating the CLI — so
 * creating one is the clearest statement of intent the site can observe, and an
 * account holding both a project and a key is one that can actually run
 * `localingos sync`. That pair is what "onboarded" means here.
 *
 * The three `cli_*_copied` events are the CLI walkthrough: install, init, sync.
 * They were previously reported as one `cli_command_copied` funnel event carrying
 * the command as a parameter, deliberately kept out of Ads as "an intent, not a
 * conversion". They are conversions now because copying `localingos sync` is the
 * last thing the website can observe before the product starts being used, and a
 * parameter on a shared event name cannot be split into separate Ads conversion
 * actions — Ads keys an action on the event, not on its parameters. The aggregate
 * `cli_command_copied` still fires alongside them for GA4 reporting.
 */
export type ConversionEventName =
  | 'sign_up'
  | 'trial_started'
  | 'project_created'
  | 'api_key_created'
  | 'activation_complete'
  | 'purchase'
  | 'cli_install_copied'
  | 'cli_init_copied'
  | 'cli_sync_copied';

/**
 * Funnel-step and intent events.
 *
 * These are not conversions and must never carry an Ads `send_to`: they are the
 * diagnostics that explain a conversion rate rather than something to bid on.
 * They are enumerated here, next to the conversions, so that a typo in an event
 * name is a compile error instead of a silently missing series in GA4 — which is
 * exactly how `cta_click_signup` came to exist in three files and fire from
 * none of them.
 */
export type FunnelEventName =
  | 'onboarding_step_reached'
  | 'cli_command_copied'
  | 'credential_copied'
  | 'signin_started'
  | 'create_project_opened'
  | 'create_api_key_opened'
  | 'api_key_create_failed'
  | 'demo_video_played'
  | 'init_details_expanded';

const isDevelopment = (): boolean => process.env.NODE_ENV === 'development';

const withoutUndefined = (params: EventParams): EventParams => {
  const result: EventParams = {};
  for (const key of Object.keys(params)) {
    if (params[key] !== undefined) result[key] = params[key];
  }
  return result;
};

export const trackEvent = (eventName: string, attributes?: EventParams): void => {
  if (window.gtag) {
    window.gtag('event', eventName, attributes);
  }

  if (isDevelopment()) {
    // eslint-disable-next-line no-console
    console.log('Analytics Event:', eventName, attributes);
  }
};

/**
 * The Google Ads conversion ID, or `undefined` when the Ads tag is not configured.
 *
 * `REACT_APP_GOOGLE_ADS_ID` is set in `.env.prod` and deliberately left unset
 * everywhere else: a real conversion ID outside production would report developer
 * traffic as customer conversions. So three things can be in the variable, and two
 * of them may not produce a request:
 *
 *  - nothing at all, in a gamma or local build;
 *  - the literal string `%REACT_APP_GOOGLE_ADS_ID%`, in public/index.html, where
 *    CRA's InterpolateHtmlPlugin leaves the placeholder untouched when the
 *    variable is unset;
 *  - a real `AW-` ID, in a production build.
 *
 * The first two fail the `AW-` shape test, so the only way to switch the Ads tag on
 * is to set a real ID.
 */
export const getGoogleAdsId = (): string | undefined => {
  const id = process.env.REACT_APP_GOOGLE_ADS_ID;
  return id && /^AW-[A-Za-z0-9_-]+$/.test(id) ? id : undefined;
};

/**
 * The per-conversion label from the Ads UI — the part after the slash in
 * `AW-123456789/AbC-D_efGh12ij`.
 *
 * Read through a switch rather than a lookup table because CRA replaces
 * `process.env.REACT_APP_*` textually at build time; a computed key would compile
 * to `process.env[name]`, which is `undefined` in the browser.
 */
const getAdsConversionLabel = (event: ConversionEventName): string | undefined => {
  switch (event) {
    case 'sign_up':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_SIGN_UP || undefined;
    case 'trial_started':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_TRIAL_STARTED || undefined;
    case 'project_created':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_PROJECT_CREATED || undefined;
    case 'api_key_created':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_API_KEY_CREATED || undefined;
    case 'activation_complete':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_ACTIVATION_COMPLETE || undefined;
    case 'purchase':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_PURCHASE || undefined;
    case 'cli_install_copied':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_CLI_INSTALL_COPIED || undefined;
    case 'cli_init_copied':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_CLI_INIT_COPIED || undefined;
    case 'cli_sync_copied':
      return process.env.REACT_APP_GOOGLE_ADS_LABEL_CLI_SYNC_COPIED || undefined;
    default:
      return undefined;
  }
};

/**
 * The `send_to` value that routes a conversion to Google Ads, or `undefined` when
 * either the account ID or this event's label is missing.
 *
 * A missing label is not an error: it is how you enable Ads reporting for some
 * milestones and not others.
 */
export const getAdsSendTo = (event: ConversionEventName): string | undefined => {
  const adsId = getGoogleAdsId();
  if (!adsId) return undefined;

  const label = getAdsConversionLabel(event);
  if (!label) return undefined;

  return `${adsId}/${label}`;
};

/**
 * Sends a conversion to GA4 and, when configured, to Google Ads.
 *
 * Two separate events on purpose. GA4 wants the recommended name (`purchase`);
 * Google Ads only recognises an event literally named `conversion` carrying a
 * `send_to` that names the conversion action. Sending one event to both
 * destinations would give Ads an event it cannot attribute.
 */
export const sendGtagConversion = (event: ConversionEventName, params: EventParams = {}): void => {
  const eventParams = withoutUndefined(params);

  if (window.gtag) {
    window.gtag('event', event, eventParams);

    const sendTo = getAdsSendTo(event);
    if (sendTo) {
      window.gtag('event', 'conversion', { ...eventParams, send_to: sendTo });
    }
  }

  if (isDevelopment()) {
    // eslint-disable-next-line no-console
    console.log('Conversion Event:', event, eventParams, 'send_to:', getAdsSendTo(event) ?? '(ads tag not configured)');
  }
};

/**
 * Moves Consent Mode from the denied defaults to the visitor's choice.
 *
 * Only ever an `update`. The `default` command is issued by public/index.html
 * before the tag initialises; re-issuing a default afterwards is ignored, and
 * issuing one here would read as though the page had never asked.
 */
export const updateGtagConsent = (granted: boolean): void => {
  if (!window.gtag) return;

  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};
